// AuthForm.js with forgot password functionality 
import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Building2, User, Mail, Lock, Timer, CheckCircle, AlertCircle, ArrowLeft, Clock, Users, BarChart3, DollarSign, CreditCard, ArrowRight, Loader, Eye, EyeOff, Info } from 'lucide-react'
import { SecurePaymentStep } from './SecurePaymentStep'

const AuthForm = ({ initialMode = 'signin', onSuccess, onBackToLanding }) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'signin')
  const [currentStep, setCurrentStep] = useState(1) // For signup flow: 1=account, 2=org, 3=plan, 4=payment
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('info') // 'success', 'error', 'info'
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false)
  const [signupEmail, setSignupEmail] = useState('')
  const [errors, setErrors] = useState({})
  
  // NEW: Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  
  const { signIn, signUp, user, resetPassword } = useAuth()
  
  // Organization and billing form data (updated structure)
  const [orgData, setOrgData] = useState({
    tenantName: '',
    industry: '',
    description: '',
    selectedPlan: null,
    nameOnCard: '',
    billingAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US'
    },
    userEmail: email,
    fullName: fullName
  })
  
  // Update orgData when email/fullName changes
  useEffect(() => {
    setOrgData(prev => ({
      ...prev,
      userEmail: email,
      fullName: fullName
    }));
  }, [email, fullName]);

  const plans = [
    {
      id: 'per_seat',
      name: 'Per-Seat Plan',
      price: 6.99,
      billing: 'per seat/month',
      stripe_price_id: 'price_1Rqyw4Pot5ny8B2Gip47fS5k',
      features: [
        'Unlimited time tracking',
        'Team management',
        'Basic reports',
        'Pay per active seat'
      ],
      description: 'Perfect for growing teams',
      popular: false,
      color: 'blue'
    },
    {
      id: 'small_team',
      name: 'Small Team',
      price: 49,
      billing: 'per month',
      stripe_price_id: 'price_1Rr0ZCPot5ny8B2G9Ns112wr',
      features: [
        'Up to 10 team members',
        'Advanced reports',
        'Priority support',
        'Fixed monthly cost'
      ],
      description: 'Great for small teams',
      popular: true,
      color: 'purple'
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 99,
      billing: 'per month',
      stripe_price_id: 'price_1Rr0b1Pot5ny8B2GiQNhWR0E',
      features: [
        'Up to 25 team members',
        'Advanced analytics',
        'Custom integrations',
        'Dedicated support'
      ],
      description: 'For established businesses',
      popular: false,
      color: 'green'
    }
  ];

  const industries = [
    'Technology', 'Consulting', 'Marketing & Advertising', 'Legal Services',
    'Architecture & Engineering', 'Healthcare', 'Education', 'Non-profit',
    'Manufacturing', 'Financial Services', 'Real Estate', 'Other'
  ];

  // Update form when initialMode changes
  useEffect(() => {
    setIsLogin(initialMode === 'signin')
    if (initialMode === 'signin') {
      setCurrentStep(1)
    }
  }, [initialMode])

  // Redirect to app if user is already authenticated
  useEffect(() => {
    if (user && onSuccess) {
      onSuccess()
    }
  }, [user, onSuccess])

  // Check for URL parameters on mount (existing code)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    
    const error = urlParams.get('error') || hashParams.get('error')
    const errorDescription = urlParams.get('error_description') || hashParams.get('error_description')
    
    if (error) {
      console.error('Auth error from URL:', error, errorDescription)
      
      if (error === 'server_error' && errorDescription?.includes('Error confirming user')) {
        setMessage('There was an issue confirming your email. Please try signing in with your credentials, or contact support if the problem persists.')
        setMessageType('error')
      } else {
        setMessage(decodeURIComponent(errorDescription || 'An authentication error occurred'))
        setMessageType('error')
      }
      
      window.history.replaceState({}, document.title, window.location.pathname)
    }
    
    const accessToken = hashParams.get('access_token')
    if (accessToken) {
      setMessage('Email confirmed successfully! You are now signed in.')
      setMessageType('success')
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [])

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!email.trim()) newErrors.email = 'Email is required';
      if (!password.trim() && !showForgotPassword) newErrors.password = 'Password is required';
      if (!isLogin && !fullName.trim()) newErrors.fullName = 'Full name is required';
    }

    if (step === 2) {
      if (!orgData.tenantName.trim()) newErrors.tenantName = 'Organization name is required';
      if (!orgData.industry) newErrors.industry = 'Please select an industry';
    }

    if (step === 3) {
      if (!orgData.selectedPlan) newErrors.selectedPlan = 'Please select a plan';
    }

    if (step === 4) {
      if (!orgData.nameOnCard.trim()) newErrors.nameOnCard = 'Name on card is required';
      if (!orgData.billingAddress.street.trim()) newErrors.street = 'Street address is required';
      if (!orgData.billingAddress.city.trim()) newErrors.city = 'City is required';
      if (!orgData.billingAddress.state.trim()) newErrors.state = 'State is required';
      if (!orgData.billingAddress.zipCode.trim()) newErrors.zipCode = 'Zip code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const formatExpiryDate = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return `${v.substring(0, 2)}/${v.substring(2, 4)}`;
    }
    return v;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault()
    if (!validateStep(1)) return;
    
    setLoading(true)
    setMessage('')
    setMessageType('info')

    try {
      const { data, error } = await signIn(email, password)
      if (error) throw new Error(error)
      setMessage('Successfully logged in!')
      setMessageType('success')
      setTimeout(() => {
        if (onSuccess) onSuccess()
      }, 1000)
    } catch (error) {
      setMessage(error.message)
      setMessageType('error')
    } finally {
      setLoading(false)
    }
  }

  // NEW: Handle forgot password submission
  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault()
    if (!email.trim()) {
      setErrors({ email: 'Email is required' })
      return
    }
    
    setLoading(true)
    setMessage('')
    setMessageType('info')

    try {
      const { error, message: successMessage } = await resetPassword(email)
      if (error) throw new Error(error)
      
      setMessage(successMessage)
      setMessageType('success')
      setShowForgotPassword(false)
      // Clear form
      setEmail('')
      setPassword('')
    } catch (error) {
      setMessage(error.message)
      setMessageType('error')
    } finally {
      setLoading(false)
    }
  }

  const handleSecurePaymentComplete = (securePaymentData) => {
    console.log('Secure payment completed:', securePaymentData);
    
    // Store secure payment data (no raw card info)
    const billingData = {
      selectedPlan: orgData.selectedPlan,
      payment_method_id: securePaymentData.payment_method_id, // ✅ Secure token only
      billingAddress: orgData.billingAddress,
      userEmail: email, // ✅ Use the email from the form
      tenantName: orgData.tenantName,
      fullName: fullName
    };
    
    console.log('Storing billing data:', billingData);
    
    // Store in localStorage temporarily
    localStorage.setItem('pendingBillingSetup', JSON.stringify(billingData));
    
    // Now proceed with user creation
    handleSignupSubmit();
  };

  const handleSignupSubmit = async () => {
    setLoading(true)
    setMessage('')
    setMessageType('info')

    try {
      // Create the user account using your existing signUp function
      const { error, message: signUpMessage } = await signUp(email, password, fullName, orgData.tenantName)
      if (error) throw new Error(error)
      
      // Show email confirmation screen
      setSignupEmail(email)
      setShowEmailConfirmation(true)
      
    } catch (error) {
      console.error('Signup error:', error)
      setMessage(error.message || 'An error occurred during signup')
      setMessageType('error')
    } finally {
      setLoading(false)
    }
  }

  const getMessageIcon = () => {
    switch (messageType) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />
      default:
        return <Info className="w-5 h-5 text-blue-600" />
    }
  }

  const getMessageStyle = () => {
    switch (messageType) {
      case 'success':
        return 'bg-green-50 text-green-700 border border-green-200'
      case 'error':
        return 'bg-red-50 text-red-700 border border-red-200'
      default:
        return 'bg-blue-50 text-blue-700 border border-blue-200'
    }
  }

  // Step indicator for signup flow
  const StepIndicator = () => {
    if (isLogin) return null;
    
    const steps = [
      { number: 1, title: 'Account', icon: User },
      { number: 2, title: 'Organization', icon: Building2 },
      { number: 3, title: 'Plan', icon: Users },
      { number: 4, title: 'Payment', icon: CreditCard }
    ];

    return (
      <div className="flex items-center justify-center mb-6">
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all ${
              currentStep >= step.number 
                ? 'bg-blue-600 border-blue-600 text-white' 
                : 'bg-white border-slate-300 text-slate-400'
            }`}>
              {currentStep > step.number ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <step.icon className="w-4 h-4" />
              )}
            </div>
            <div className="ml-2 mr-4">
              <div className={`text-xs font-medium ${
                currentStep >= step.number ? 'text-blue-600' : 'text-slate-400'
              }`}>
                {step.title}
              </div>
            </div>
            {index < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 ${
                currentStep > step.number ? 'bg-blue-600' : 'bg-slate-200'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Email Confirmation Screen (existing)
  const EmailConfirmationScreen = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <Timer className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Check Your Email!</h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            We've sent a confirmation link to <strong>{signupEmail}</strong>. 
            Click the link in your email to activate your account and access your new organization.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
          <div className="text-center">
            <Mail className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Didn't receive the email?</h3>
            <p className="text-slate-600 mb-6">
              Check your spam folder, or wait a few minutes and the email should arrive.
            </p>
            <button
              onClick={() => {
                setShowEmailConfirmation(false)
                setIsLogin(true)
                setCurrentStep(1)
              }}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-lg font-medium transition-all"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  // Email confirmation screen
  if (showEmailConfirmation) {
    return <EmailConfirmationScreen />
  }

  // LOGIN FORM
  if (isLogin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Timer className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">
              {showForgotPassword ? 'Reset Password' : 'Welcome Back'}
            </h1>
            <p className="text-slate-600 mt-2">
              {showForgotPassword 
                ? 'Enter your email to receive a password reset link'
                : 'Sign in to your account'
              }
            </p>
          </div>

          {/* NEW: Forgot Password Form */}
          {showForgotPassword && (
            <div className="space-y-6">
              <button
                onClick={() => setShowForgotPassword(false)}
                className="flex items-center text-blue-600 hover:text-blue-500 font-medium transition-colors mb-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Sign In
              </button>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                      errors.email ? 'border-red-300' : 'border-slate-300'
                    }`}
                    required
                  />
                </div>
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
              
              <button
                onClick={handleForgotPasswordSubmit}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 rounded-lg font-medium transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </div>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </div>
          )}

          {/* Regular Login Form */}
          {!showForgotPassword && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                      errors.email ? 'border-red-300' : 'border-slate-300'
                    }`}
                    required
                  />
                </div>
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password *
                </label>
                <div className="relative">
                  <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                      errors.password ? 'border-red-300' : 'border-slate-300'
                    }`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>
              
              {/* NEW: Forgot Password Link */}
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-blue-600 hover:text-blue-500 font-medium transition-colors"
                >
                  Forgot your password?
                </button>
              </div>
              
              <button
                onClick={handleLoginSubmit}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 rounded-lg font-medium transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Signing in...</span>
                  </div>
                ) : (
                  'Sign In'
                )}
              </button>
            </div>
          )}

          {/* Message Display */}
          {message && (
            <div className={`mt-6 text-center text-sm p-3 rounded-lg flex items-center space-x-2 ${getMessageStyle()}`}>
              {getMessageIcon()}
              <span className="flex-1">{message}</span>
            </div>
          )}
          
          {/* Toggle between Login/Signup */}
          {!showForgotPassword && (
            <div className="text-center mt-6">
              <button
                onClick={() => {
                  setIsLogin(false)
                  setCurrentStep(1)
                  setMessage('')
                  setMessageType('info')
                  setErrors({})
                }}
                className="text-blue-600 hover:text-blue-500 font-medium transition-colors"
              >
                Don't have an account? Create your organization
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  // SIGNUP FLOW WITH STEPS (keeping all your existing signup functionality)
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="px-8 py-6 bg-gradient-to-r from-blue-600 to-purple-600">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                  <Timer className="w-6 h-6 text-blue-600" />
                </div>
                <h1 className="text-2xl font-bold text-white">Create Your Organization</h1>
              </div>
              {onBackToLanding && (
                <button
                  onClick={onBackToLanding}
                  className="text-white hover:text-blue-100 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          <div className="p-8">
            <StepIndicator />

            <div className="max-w-2xl mx-auto">
              {/* Step 1: Account Details */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Create your account</h2>
                    <p className="text-slate-600">Let's start with your personal information</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <div className="relative">
                      <User className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="John Doe"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                          errors.fullName ? 'border-red-300' : 'border-slate-300'
                        }`}
                        required
                      />
                    </div>
                    {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                      <input
                        type="email"
                        placeholder="john@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                          errors.email ? 'border-red-300' : 'border-slate-300'
                        }`}
                        required
                      />
                    </div>
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Password *
                    </label>
                    <div className="relative">
                      <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                      <input
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                          errors.password ? 'border-red-300' : 'border-slate-300'
                        }`}
                        required
                      />
                    </div>
                    {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                    <p className="text-xs text-slate-500 mt-1">Password must be at least 6 characters long</p>
                  </div>
                </div>
              )}

              {/* Step 2: Organization Setup */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Set up your organization</h2>
                    <p className="text-slate-600">Tell us about your business</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Organization Name *
                    </label>
                    <div className="relative">
                      <Building2 className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Acme Inc."
                        value={orgData.tenantName}
                        onChange={(e) => setOrgData(prev => ({ ...prev, tenantName: e.target.value }))}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                          errors.tenantName ? 'border-red-300' : 'border-slate-300'
                        }`}
                        required
                      />
                    </div>
                    {errors.tenantName && <p className="text-red-500 text-sm mt-1">{errors.tenantName}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Industry *
                    </label>
                    <select
                      value={orgData.industry}
                      onChange={(e) => setOrgData(prev => ({ ...prev, industry: e.target.value }))}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                        errors.industry ? 'border-red-300' : 'border-slate-300'
                      }`}
                      required
                    >
                      <option value="">Select an industry</option>
                      {industries.map(industry => (
                        <option key={industry} value={industry}>{industry}</option>
                      ))}
                    </select>
                    {errors.industry && <p className="text-red-500 text-sm mt-1">{errors.industry}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Description (Optional)
                    </label>
                    <textarea
                      placeholder="Brief description of your organization..."
                      value={orgData.description}
                      onChange={(e) => setOrgData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Choose Plan */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Choose your plan</h2>
                    <p className="text-slate-600">Select the plan that best fits your team</p>
                  </div>

                  <div className="grid gap-6">
                    {plans.map((plan) => (
                      <div
                        key={plan.id}
                        onClick={() => setOrgData(prev => ({ ...prev, selectedPlan: plan }))}
                        className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all hover:border-blue-300 hover:shadow-lg ${
                          orgData.selectedPlan?.id === plan.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200'
                        } ${plan.popular ? 'ring-2 ring-purple-500 ring-opacity-50' : ''}`}
                      >
                        {plan.popular && (
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                            <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                              Most Popular
                            </span>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                            <p className="text-slate-600">{plan.description}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-3xl font-bold text-slate-900">${plan.price}</div>
                            <div className="text-sm text-slate-600">{plan.billing}</div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          {plan.features.map((feature, index) => (
                            <div key={index} className="flex items-center space-x-3">
                              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-slate-700">{feature}</span>
                            </div>
                          ))}
                        </div>

                        {orgData.selectedPlan?.id === plan.id && (
                          <div className="absolute top-4 right-4">
                            <CheckCircle className="w-6 h-6 text-blue-500" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {errors.selectedPlan && <p className="text-red-500 text-sm mt-1">{errors.selectedPlan}</p>}
                </div>
              )}

              {/* Step 4: Payment */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">Payment information</h2>
                    <p className="text-slate-600">Secure payment processing with Stripe</p>
                  </div>

                  {orgData.selectedPlan && (
                    <div className="bg-slate-50 rounded-lg p-4 mb-6">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-semibold text-slate-900">{orgData.selectedPlan.name}</h3>
                          <p className="text-slate-600">{orgData.selectedPlan.description}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-slate-900">${orgData.selectedPlan.price}</div>
                          <div className="text-sm text-slate-600">{orgData.selectedPlan.billing}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <SecurePaymentStep
                    selectedPlan={orgData.selectedPlan}
                    billingAddress={orgData.billingAddress}
                    setBillingAddress={(address) => setOrgData(prev => ({ ...prev, billingAddress: address }))}
                    onPaymentComplete={handleSecurePaymentComplete}
                    loading={loading}
                  />
                </div>
              )}
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
              <button
                onClick={currentStep === 1 ? () => {
                  setIsLogin(true)
                  setCurrentStep(1)
                } : prevStep}
                className="flex items-center space-x-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 px-6 py-3 rounded-lg font-medium transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>{currentStep === 1 ? 'Sign In Instead' : 'Back'}</span>
              </button>

              {currentStep < 4 ? (
                <button
                  onClick={nextStep}
                  className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-lg font-medium transition-all"
                >
                  <span>Continue</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              ) : (
                <div className="text-slate-600 text-sm">
                  Complete payment above to finish setup
                </div>
              )}
            </div>

            {message && (
              <div className={`mt-6 text-center text-sm p-3 rounded-lg flex items-center space-x-2 ${getMessageStyle()}`}>
                {getMessageIcon()}
                <span className="flex-1">{message}</span>
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-6">
          <p className="text-slate-600 text-sm">
            By creating an organization, you agree to our{' '}
            <a href="#" className="text-blue-600 hover:underline">Terms of Service</a>{' '}
            and{' '}
            <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default AuthForm